import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";

function PresetEditor() {
  const [presets, setPresets] = useState(null);
  const [adding, setAdding] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setPresets(r.data.quick_hour_presets || [])).catch(() => setPresets([]));
  }, []);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= presets.length) return;
    const next = [...presets];
    [next[i], next[j]] = [next[j], next[i]];
    setPresets(next);
  };
  const remove = (i) => setPresets(presets.filter((_, idx) => idx !== i));
  const add = () => {
    const v = Number(adding);
    if (!(v > 0)) return;
    setPresets([...presets, Math.round(v * 100) / 100]);
    setAdding("");
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/settings", { quick_hour_presets: presets });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (presets === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="preset-editor">
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((p, i) => (
          <div key={i} data-testid={`preset-chip-${i}`} className="flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1">
            <span className="nums text-sm font-medium text-foreground">+{p}</span>
            <button type="button" onClick={() => move(i, -1)} data-testid={`preset-up-${i}`} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} data-testid={`preset-down-${i}`} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === presets.length - 1}><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => remove(i)} data-testid={`preset-remove-${i}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {presets.length === 0 && <span className="text-sm text-muted-foreground">No presets yet.</span>}
      </div>
      <div className="flex items-center gap-2">
        <Input type="number" step="0.1" min="0" value={adding} onChange={(e) => setAdding(e.target.value)} data-testid="preset-input" placeholder="e.g. 0.3" className="max-w-[140px] nums" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button variant="secondary" size="sm" onClick={add} data-testid="preset-add"><Plus className="h-4 w-4" /> Add</Button>
        <div className="ml-auto flex items-center gap-2">
          {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
          <Button size="sm" onClick={save} disabled={saving} data-testid="preset-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save presets
          </Button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function PresetsCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick-hour presets</CardTitle>
        <CardDescription>Shared across the Workbench and the Quick Time Log. Reorder or remove as you like.</CardDescription>
      </CardHeader>
      <CardContent><PresetEditor /></CardContent>
    </Card>
  );
}
